"use client"

import * as React from "react"
import { useNavigate } from "react-router"

import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu"
import { useProduct } from "@/contexts/product-context"
import {
  EXAM_MANAGEMENT_ADMIN_HOME_PATH,
  EXAM_MANAGEMENT_MY_EXAMS_PATH,
} from "@/lib/exam-management-paths"
import {
  getLoginSession,
  openableIdentities,
  openAs,
  WORKSPACE_ROLES,
  type WorkspaceRole,
} from "@/lib/login-session"

function identityLabel(role: WorkspaceRole): string {
  return WORKSPACE_ROLES.find(entry => entry.value === role)?.label ?? role
}

/** Switch between identities this session holds (e.g. student + school) without signing out. */
export function SessionIdentityMenuItem() {
  const { product } = useProduct()
  const navigate = useNavigate()
  const [, bump] = React.useReducer((n: number) => n + 1, 0)

  React.useEffect(() => {
    const handler = () => bump()
    window.addEventListener("exxat-login-session-changed", handler)
    return () => window.removeEventListener("exxat-login-session-changed", handler)
  }, [])

  const identities = openableIdentities()
  if (identities.length <= 1) return null

  const session = getLoginSession()

  function switchIdentity(role: WorkspaceRole) {
    openAs(role)
    if (product !== "exxat-exam-management") return
    navigate(
      role === "student"
        ? EXAM_MANAGEMENT_MY_EXAMS_PATH
        : EXAM_MANAGEMENT_ADMIN_HOME_PATH,
      { replace: true },
    )
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <i className="fa-light fa-user-tag" aria-hidden="true" />
        Open as
        <span className="ms-auto pe-1 text-xs text-muted-foreground">
          {identityLabel(session.role)}
        </span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-56">
        <DropdownMenuLabel>Current session</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={session.role}
          onValueChange={value => switchIdentity(value as WorkspaceRole)}
        >
          {identities.map(role => (
            <DropdownMenuRadioItem key={role} value={role}>
              {identityLabel(role)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}
